import * as React from "react";
import { useState } from "react";
import QuestionEditor from "./QuestionEditor";
import {
  IconPlus,
  SecondaryBlueButton,
  Survey,
  SurveyQuestion,
  TertiaryBlueButton
} from "@velaro/velaro-component-lib";
import IconChevronDown from "@/icons/IconChevronDown";
import IconChevronUp from "@/icons/IconChevronUp";

interface Props {
  survey: Survey;
  errors: Record<string, string>[];
  netSuiteMappings?: any[];
  loadingMappings?: boolean;
  update(survey: Survey): void;
  remove(): void;
}

export default function SurveyEditor({
  survey,
  errors,
  netSuiteMappings = [],
  loadingMappings = false,
  update,
  remove
}: Props) {
  const [showVariableHelp, setShowVariableHelp] = useState(false);

  function addQuestion() {
    update({
      ...survey,
      questions: [
        ...survey.questions,
        {
          type: "name",
          text: "What is your name?",
          required: true,
          variable: "name",
          options: [],
          crmOptions: {}
        }
      ]
    });
  }

  function updateQuestion(index: number, question: SurveyQuestion) {
    if (question.type === "label") {
      question.options = [];
      question.required = false;
      question.variable = undefined;
    }
    survey.questions[index] = question;
    update({ ...survey });
  }

  const hasQuestions = survey.questions.length > 0;
  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-2">
        <input
          type="text"
          className="input"
          required
          value={survey.name}
          onChange={(e) => update({ ...survey, name: e.currentTarget.value })}
        />
        <TertiaryBlueButton label="Delete survey" onClick={remove} />
      </div>

      {!hasQuestions && (
        <div className="pt-4 pb-4">This survey has no questions.</div>
      )}
      {hasQuestions && (
        <>
          <button
            type="button"
            className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700 mb-2"
            onClick={() => setShowVariableHelp((v) => !v)}
          >
            {showVariableHelp ? <IconChevronUp /> : <IconChevronDown />}
            <span>How do survey fields pass data to rules &amp; workflows?</span>
          </button>
          {showVariableHelp && (
            <div className="bg-blue-50 border border-blue-200 rounded p-3 text-sm text-slate-700 mb-3 leading-relaxed">
              <p className="font-semibold mb-2">Each field stores its answer as a variable</p>
              <p className="mb-2">
                When a visitor submits the survey, every field&apos;s answer is saved under the{" "}
                <span className="font-semibold">Variable</span> name you set.
                Reference it anywhere using{" "}
                <code className="bg-blue-100 text-blue-700 px-1 rounded font-mono text-xs">
                  {"<<variable_name>>"}
                </code>
                .
              </p>
              <p className="font-semibold mb-1">Where you can use these variables:</p>
              <ul className="list-disc ml-4 space-y-1">
                <li>
                  <span className="font-semibold">Rule conditions</span> — add an &quot;Attribute&quot; condition and match the variable name to route chats, assign agents, or trigger actions
                </li>
                <li>
                  <span className="font-semibold">Workflow nodes</span> — insert into Send Message, HTTP Request, and Send Email nodes using{" "}
                  <code className="bg-blue-100 text-blue-700 px-1 rounded font-mono text-xs">
                    {"<<variable_name>>"}
                  </code>
                </li>
                <li>
                  <span className="font-semibold">Set Data nodes</span> — copy values between variables or write to contact fields
                </li>
              </ul>
              <p className="mt-2 text-xs text-slate-500">
                Tip: Fields like{" "}
                <code className="font-mono text-xs">name</code>,{" "}
                <code className="font-mono text-xs">email</code>, and{" "}
                <code className="font-mono text-xs">language</code>{" "}
                have fixed variable names set automatically.
              </p>
            </div>
          )}
          <table className="border-separate border-spacing-6">
            <thead>
              <tr>
                <th className="text-left">Label</th>
                <th className="text-left">Type</th>
                <th className="text-left">
                  <span className="flex items-center gap-1">
                    Variable
                    <span
                      title="The name used to reference this field's answer in rules and workflows, e.g. <<variable_name>>"
                      className="cursor-help text-slate-400 text-xs"
                    >
                      ⓘ
                    </span>
                  </span>
                </th>
                <th className="text-left">Required</th>
                <th className="text-left"></th>
              </tr>
            </thead>
            <tbody>
              {survey.questions.map((q, i) => (
                <QuestionEditor
                  errors={errors[i] || {}}
                  key={i}
                  question={q}
                  netSuiteMappings={netSuiteMappings}
                  loadingMappings={loadingMappings}
                  update={(q) => updateQuestion(i, q)}
                  remove={() => {
                    survey.questions.splice(i, 1);
                    update({ ...survey });
                  }}
                />
              ))}
            </tbody>
          </table>
        </>
      )}
      <div>
        <SecondaryBlueButton
          prefixIcon={<IconPlus />}
          label="Add Question"
          onClick={addQuestion}
        />
      </div>
    </div>
  );
}
